import { useEffect, useCallback, useMemo } from 'react';
import { useAtom } from 'jotai';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/Download';
import { containedButtonStyle } from '@/theme';
import { PageLoader } from '../shared/PageLoader';
import { TemplateForm } from './TemplateForm';
import { FormTypes } from './constants';
import { selectedStructure } from './state';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import {
  GetStructureDetailsDocument,
  useAddStructurePipesMutation,
  useGetStructureDetailsQuery,
} from '@/__generated__/graphql';

type StructureTemplateProps = {
  structureId: string;
};

export const StructureTemplate = ({ structureId }: StructureTemplateProps) => {
  const [structure, setStructure] = useAtom(selectedStructure);
  const [addPipesToStructure] = useAddStructurePipesMutation();
  const { data: structureData } = useGetStructureDetailsQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'cache-first',
  });

  const structurePipesData = useMemo(() => {
    return structureData?.structure?.structurePipesConfig?.imperialSteelPipes;
  }, [structureData?.structure?.structurePipesConfig?.imperialSteelPipes]);

  useEffect(() => {
    setStructure(structureData?.structure);
  }, [setStructure, structureData?.structure]);

  const downloadJson = useCallback(() => {
    const jsonData = JSON.stringify(structure, undefined, 2);
    const jsonBlob = new Blob([jsonData], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(jsonBlob);
    const fileName = `${structure?.name ?? 'Platform'}-${Date.now().toString()}.json`;
    link.download = fileName;
    document.body.append(link);
    link.click();
    link.remove();
  }, [structure]);

  const addPipesHanlder = () => {
    addPipesToStructure({
      variables: {
        input: {
          structureId,
        },
      },
      refetchQueries: [GetStructureDetailsDocument],
    });
  };
  return (
    <Stack>
      <Stack
        direction="row"
        gap={2}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          pt: 4,
          alignItems: 'center',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            ml: 'auto',
            flexShrink: 0,
          }}
        >
          <Button
            startIcon={<DownloadIcon />}
            variant="contained"
            onClick={() => downloadJson()}
            sx={containedButtonStyle}
          >
            Download JSON
          </Button>
          <Button
            startIcon={<AddCircleIcon />}
            variant="contained"
            onClick={() => addPipesHanlder()}
            sx={containedButtonStyle}
          >
            Add Structure Pipes
          </Button>
        </Stack>
      </Stack>
      {!structure && <PageLoader />}
      {structure && (
        <TemplateForm
          currentTemplateType={FormTypes.Structure.toString()}
          structureId={structure?.id}
          structurePipesData={structurePipesData}
        />
      )}
    </Stack>
  );
};
